import { gql } from "graphql-request";
import { createGraphQLClient } from "./graphql.service";
import { toFilterTree, type GroupNode } from "@/components/filter/filter-tree";

export interface EntityConnectInput {
  id: string;
}

export type ColumnAggregateFunction = "sum" | "avg" | "min" | "max" | "count";

export interface ColumnStateItem {
  /** Scalar field name (e.g. "name") or dotted path into a to-1 relation (e.g. "standort.city"). */
  id: string;
  visible: boolean;
  size?: number;
  aggregate?: ColumnAggregateFunction;
  /** @deprecated Read-only field used for one-shot migration of legacy views. New writes never set this. */
  fieldToFetchAndDisplay?: string;
}

export interface SortingState {
  field: string;
  order: "asc" | "desc";
}

/** Render layout for an entity table view. "table" is the classic data grid;
 * "timeline" renders each instance as a bar on a horizontally-scrolling Gantt;
 * "kanban" groups instances into columns by an enum field. */
export type TableLayout = "table" | "timeline" | "kanban";

export interface ColumnState {
  columns?: ColumnStateItem[]; // Array of columns with their visibility state and size, order is implicit
  sorting?: SortingState[]; // Array of sorting states
  searchFieldPath?: string; // Field path used for search functionality
  /** Active render layout. Absent/undefined means "table" (back-compat with existing views). */
  layout?: TableLayout;
  /** Timeline layout: scalar DATE/DATETIME field name used as the bar start. */
  timelineStartField?: string;
  /** Timeline layout: scalar DATE/DATETIME field name used as the bar end. */
  timelineEndField?: string;
  /** Timeline layout: field name shown in the fixed left sidebar (the row label). */
  timelineLabelField?: string;
  /** Timeline layout: persist bar drags/placements immediately instead of staging them
   * as pending edits behind the "Save all" overlay. Absent means off. */
  timelineAutoSave?: boolean;
  /** Timeline layout: span (in days, inclusive) given to an item placed on an empty row.
   * Absent falls back to the built-in default. */
  timelineDefaultDays?: number;
  /** Kanban layout: enum or to-one relation field whose values become the board columns. */
  kanbanGroupField?: string;
  /** Kanban layout: field name shown as the card title. */
  kanbanLabelField?: string;
  /** Kanban layout: persist card moves immediately instead of staging them as pending
   * edits behind the "Save all" overlay. Absent means off. */
  kanbanAutoSave?: boolean;
  /** Kanban layout: persisted column order (enum values / relation ids, plus the "unset"
   * sentinel). Keys absent from this list fall to the end in their natural order. */
  kanbanColumnOrder?: string[];
  /** Kanban layout: per-column totals shown in every column's footer. */
  kanbanAggregates?: KanbanAggregate[];
}

export interface KanbanAggregate {
  function: ColumnAggregateFunction;
  /** A number field on the board's entity or a dotted relation path to one; `count` takes
   * `<relation>.id`. */
  field: string;
  /** Whether a related record shared by several cards counts once per column; unset takes
   * the backend default (count: true, others: false). */
  distinct?: boolean;
}

export interface CreateDashboardViewInput {
  name: string;
  description?: string;
  entity?: EntityConnectInput;
  filter?: GroupNode;
  columnState?: ColumnState;
  pylo_aros?: Array<EntityConnectInput>;
  index?: number;
}

export interface DashboardView extends CreateDashboardViewInput {
  id: string;
}

interface DashboardViewResponse {
  id: string;
  name: string;
  description?: string;
  index?: number;
  entity?: {
    data: {
      id: string;
    };
  };
  filter?: string;
  column_state?: string;
  pylo_aros?: {
    data: Array<{
      id: string;
      entity?: {
        data: {
          name: string;
        };
      };
      pylo_users?: {
        data: Array<{
          email: string;
          pylo_api_key?: {
            data: {
              name: string;
            };
          };
        }>;
      };
    }>;
  };
}

export interface DashboardViewListResponse {
  data: DashboardView[];
  fetchedAt: Date;
}

// Legacy views stored relation columns as { id: "standort", fieldToFetchAndDisplay: "name" }.
// Path-as-id views store the same as { id: "standort.name" }. Migrate on read so older views render.
// A column without fieldToFetchAndDisplay and without a dot is left untouched here; the table fills
// in a default leaf for bare to-1 relation ids once the schema is available.
const migrateColumnState = (state: ColumnState | undefined): ColumnState | undefined => {
  if (!state?.columns) return state;
  const upgraded = state.columns.map((col) => {
    if (col.id.includes(".")) {
      if (col.fieldToFetchAndDisplay) {
        const { fieldToFetchAndDisplay: _, ...rest } = col;
        return rest;
      }
      return col;
    }
    if (col.fieldToFetchAndDisplay) {
      const { fieldToFetchAndDisplay, ...rest } = col;
      return { ...rest, id: `${col.id}.${fieldToFetchAndDisplay}` };
    }
    return col;
  });
  // Drop duplicates the upgrade may have introduced (e.g. legacy {id:"x", fieldToFetchAndDisplay:"y"}
  // alongside an already-saved {id:"x.y"}). Keep the first occurrence.
  const seen = new Set<string>();
  const columns = upgraded.filter((col) => {
    if (seen.has(col.id)) return false;
    seen.add(col.id);
    return true;
  });
  return { ...state, columns };
};

const parseDashboardView = (response: DashboardViewResponse): DashboardView => ({
  id: response.id,
  name: response.name,
  description: response.description,
  index: response.index,
  entity: response.entity?.data,
  filter: toFilterTree(response.filter ? JSON.parse(response.filter) : undefined),
  columnState: response.column_state
    ? migrateColumnState(JSON.parse(response.column_state) as ColumnState)
    : undefined,
  pylo_aros: response.pylo_aros?.data?.map((aro) => ({ id: aro.id })) || [],
});

const parseDashboardViewList = (response: {
  data: DashboardViewResponse[];
}): DashboardViewListResponse => ({
  data: response.data.map(parseDashboardView),
  fetchedAt: new Date(),
});

const dashboardViewListQueryDocument = gql`
  query DashboardViewList($entityId: String) {
    dashboardViewList(
      filter: { query: { condition: { field: "entity.id", operator: equal, value: $entityId } } }
    ) {
      data {
        id
        name
        description
        index
        entity {
          data {
            id
          }
        }
        filter
        column_state
        pylo_aros {
          data {
            id
            entity {
              data {
                name
              }
            }
            pylo_users {
              data {
                email
                pylo_api_key {
                  data {
                    name
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

const createDashboardViewMutationDocument = gql`
  mutation CreateDashboardView($input: CreateDashboardViewInput!) {
    createDashboardView(input: $input) {
      data {
        id
        name
        description
        index
        entity {
          data {
            id
          }
        }
        filter
        column_state
        pylo_aros {
          data {
            id
            entity {
              data {
                name
              }
            }
            pylo_users {
              data {
                email
                pylo_api_key {
                  data {
                    name
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

const updateDashboardViewMutationDocument = gql`
  mutation UpdateDashboardView($input: UpdateDashboardViewInput!) {
    updateDashboardView(input: $input) {
      data {
        id
        name
        description
        index
        entity {
          data {
            id
          }
        }
        filter
        column_state
        pylo_aros {
          data {
            id
            entity {
              data {
                name
              }
            }
            pylo_users {
              data {
                email
                pylo_api_key {
                  data {
                    name
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

const deleteDashboardViewMutationDocument = gql`
  mutation DeleteDashboardView($ids: [ID!]!) {
    deleteDashboardView(ids: $ids) {
      data {
        success
      }
    }
  }
`;

export const getDashboardViewList = async (
  entityId?: string,
): Promise<DashboardViewListResponse> => {
  try {
    const client = createGraphQLClient({ endpointName: "dashboardViewList" });
    const result = (await client.request(dashboardViewListQueryDocument, {
      entityId,
    })) as {
      dashboardViewList: {
        data: DashboardViewResponse[];
      };
    };

    return parseDashboardViewList(result.dashboardViewList);
  } catch (error) {
    console.error("error fetching dashboard views", error);
    throw error;
  }
};

export const createDashboardView = async ({
  name,
  description,
  entity,
  filter,
  columnState,
  pylo_aros,
  index,
}: CreateDashboardViewInput): Promise<DashboardView> => {
  const client = createGraphQLClient({ endpointName: "createDashboardView" });

  try {
    const input = {
      name,
      description,
      entity_set: entity,
      filter: JSON.stringify(filter ?? []),
      column_state: columnState ? JSON.stringify(columnState) : undefined,
      pylo_aros_set: pylo_aros?.map((aro) => ({ id: aro.id })),
      index,
    };

    const result = (await client.request(createDashboardViewMutationDocument, {
      input,
    })) as {
      createDashboardView: {
        data: DashboardViewResponse;
      };
    };

    return parseDashboardView(result.createDashboardView.data);
  } catch (error) {
    console.error("error creating dashboard view", error);
    throw error;
  }
};

export const updateDashboardView = async ({
  id,
  name,
  description,
  entity,
  filter,
  columnState,
  pylo_aros,
  index,
}: Partial<CreateDashboardViewInput> & {
  id: string;
}): Promise<DashboardView> => {
  const client = createGraphQLClient({ endpointName: "updateDashboardView" });

  try {
    const input = {
      id,
      name,
      description,
      entity,
      filter: !!filter ? JSON.stringify(filter ?? []) : undefined,
      column_state: columnState ? JSON.stringify(columnState) : undefined,
      pylo_aros_set: pylo_aros?.map((aro) => ({ id: aro.id })),
      index,
    };

    const result = (await client.request(updateDashboardViewMutationDocument, {
      input,
    })) as {
      updateDashboardView: {
        data: DashboardViewResponse;
      };
    };

    return parseDashboardView(result.updateDashboardView.data);
  } catch (error) {
    console.error("error updating dashboard view", error);
    throw error;
  }
};

export const deleteDashboardView = async (id: string) => {
  const client = createGraphQLClient({ endpointName: "deleteDashboardView" });

  try {
    const result = (await client.request(deleteDashboardViewMutationDocument, {
      ids: [id],
    })) as {
      deleteDashboardView: {
        data: {
          success: boolean;
        };
      };
    };

    return result.deleteDashboardView.data ?? { success: false };
  } catch (error) {
    console.error("error deleting dashboard view", error);
    throw error;
  }
};

// Helper function to convert between column state and table state
export const columnStateToTableState = (columnState: ColumnState | undefined) => {
  if (!columnState) {
    return { order: [], visibility: {}, sorting: [] };
  }

  const order = columnState.columns?.map((col) => col.id) ?? [];
  const visibility: Record<string, boolean> = {};

  // Set visibility for all columns
  columnState.columns?.forEach((col) => {
    // Default to true if not specified
    visibility[col.id] = col.visible ?? true;
  });

  return {
    order,
    visibility,
    sorting: columnState.sorting ?? [],
  };
};

// Helper function to compare sorting states
export const areSortingEqual = (sorting1: SortingState[], sorting2: SortingState[]): boolean => {
  if (sorting1.length !== sorting2.length) {
    return false;
  }

  return sorting1.every((sort, index) => {
    const otherSort = sorting2[index];
    return sort.field === otherSort.field && sort.order === otherSort.order;
  });
};
